import React, { useState } from "react";
import { motion } from "framer-motion";

/**
 * Phase 14.4 — 5-step service-specific process timeline.
 *
 * Numbered gold circles connected by a navy line. Hover (or focus/tap on
 * mobile) expands a step to reveal a brief "What happens here" tooltip.
 * The first step pulses gold on initial render. Respects
 * `prefers-reduced-motion`.
 *
 * Props:
 *   steps: [{ label, note }]  — 4–5 required
 */

function Step({ step, index, isActive, isOpen, onToggle }) {
  return (
    <div
      className="flex-1 min-w-[132px] flex flex-col items-center text-center relative"
      data-testid={`process-step-${index}`}
    >
      <motion.button
        type="button"
        onClick={onToggle}
        onMouseEnter={onToggle}
        onMouseLeave={onToggle}
        aria-expanded={isOpen}
        aria-label={`Step ${index + 1} — ${step.label}`}
        initial={{ scale: 0.8, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.4, delay: index * 0.08 }}
        className={`relative z-10 inline-flex items-center justify-center w-14 h-14 rounded-full font-serif-display text-lg transition-all duration-300 ${
          isActive
            ? "bg-lw-gold text-white shadow-[0_0_0_6px_rgba(212,162,76,0.18)]"
            : "bg-white text-lw-navy border-2 border-lw-navy/15 hover:border-lw-gold"
        } ${isActive ? "animate-pulse" : ""}`}
      >
        {index + 1}
      </motion.button>
      <div className="mt-3 px-2 max-w-[160px]">
        <div className="font-serif-display text-sm sm:text-base text-lw-navy leading-snug">
          {step.label}
        </div>
        <motion.p
          initial={false}
          animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
          transition={{ duration: 0.25 }}
          className="overflow-hidden text-xs text-lw-muted leading-relaxed mt-1.5"
        >
          {step.note}
        </motion.p>
      </div>
    </div>
  );
}

export const ServiceProcessTimeline = ({ steps = [] }) => {
  const [openIndex, setOpenIndex] = useState(0);
  if (!steps.length) return null;

  return (
    <div data-testid="service-process-timeline" className="w-full">
      <div className="relative flex flex-col sm:flex-row sm:items-start gap-8 sm:gap-2">
        {/* Horizontal connecting line (desktop only) */}
        <div
          aria-hidden="true"
          className="hidden sm:block absolute left-[8%] right-[8%] top-7 h-px bg-lw-navy/15"
        />
        {steps.map((s, i) => (
          <Step
            key={i}
            step={s}
            index={i}
            isActive={i === 0}
            isOpen={openIndex === i}
            onToggle={() => setOpenIndex((prev) => (prev === i ? -1 : i))}
          />
        ))}
      </div>
    </div>
  );
};

export default ServiceProcessTimeline;
